import { Edit2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GenieIcon } from '@/components/ui/genie-icons/GenieIcon';
import type { GenieIconName } from '@/components/ui/genie-icons/genie-icons';
import { readableOnWhite } from '@/utils/branding/brandContrast';

interface OverviewSectionCardProps {
  title: string;
  subtitle?: string;
  genieIcon?: GenieIconName;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  children: React.ReactNode;
  brand?: string;
  secondaryColor?: string;
  gradientEnabled?: boolean;
  gradientAngle?: number;
}

export const OverviewSectionCard = ({
  title,
  subtitle,
  genieIcon,
  onEdit,
  onSave,
  onCancel,
  isSaving,
  children,
  brand: rawBrand = '#4E9CFF',
  secondaryColor: rawSecondaryColor,
  gradientEnabled,
  gradientAngle,
}: OverviewSectionCardProps) => {
  const brand = readableOnWhite(rawBrand);
  const secondaryColor = rawSecondaryColor ? readableOnWhite(rawSecondaryColor, brand) : undefined;

  const iconProps = {
    size: 28,
    color: brand,
    secondaryColor,
    gradientEnabled,
    gradientAngle,
  };
  const headerIcon = genieIcon ? <GenieIcon name={genieIcon} {...iconProps} /> : null;

  const editControls = onSave ? (
    <div className="flex items-center gap-2 shrink-0">
      <Button type="button" variant="outline" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={onSave}
        disabled={isSaving}
        className="gap-1.5 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:opacity-90 hover:scale-105 transition-all"
      >
        <Save className="w-4 h-4" />
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </div>
  ) : onEdit ? (
    <Button
      onClick={onEdit}
      variant="outline"
      size="sm"
      className="flex items-center gap-2 hover:scale-105 transition-transform shrink-0"
    >
      <Edit2 className="w-4 h-4" />
      Edit
    </Button>
  ) : null;

  const editControlsMobile = onSave ? (
    <div className="flex items-center gap-2 shrink-0">
      <Button type="button" variant="outline" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={onSave}
        disabled={isSaving}
        className="gap-1.5 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:opacity-90 hover:scale-105 transition-all"
      >
        <Save className="w-4 h-4" />
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </div>
  ) : onEdit ? (
    <Button
      onClick={onEdit}
      variant="outline"
      size="icon"
      className="hover:scale-105 transition-transform shrink-0"
      aria-label="Edit"
    >
      <Edit2 className="w-4 h-4" />
    </Button>
  ) : null;

  return (
    <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
      <div className="rounded-[10px] bg-white overflow-hidden h-full">
        {/* Header */}
        <div
          className="px-4 md:px-6 py-4 md:py-5 border-b"
          style={{ background: `${brand}05`, borderColor: `${brand}15` }}
        >
          {/* Mobile */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between gap-3 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                {headerIcon}
                <h3 className="text-xl font-bold text-gray-900 truncate">{title}</h3>
              </div>
              {!onSave && editControlsMobile}
            </div>
            {subtitle && <p className="text-sm text-gray-600 mt-1.5 leading-snug">{subtitle}</p>}
            {onSave && <div className="mt-3">{editControlsMobile}</div>}
          </div>

          {/* Desktop */}
          <div className="hidden sm:flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {headerIcon}
              <div className="min-w-0">
                <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            {editControls}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
};
